import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  PasswordInput,
  PasswordInputProps,
  Popover,
  Progress,
  Text,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';

function PasswordRequirement({
  meets,
  label,
}: {
  meets: boolean;
  label: string;
}) {
  return (
    <Box
      c={meets ? 'teal' : 'red'}
      style={{ display: 'flex', alignItems: 'center' }}
      mt={7}
    >
      {meets ? <IconCheck size={14} /> : <IconX size={14} />}
      <Text ml={10} size="sm">
        {label}
      </Text>
    </Box>
  );
}

export default function StrongPasswordInput(props: PasswordInputProps) {
  const { t } = useTranslation();
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [value, setValue] = useState('');

  const requirements = [
    { re: /[0-9]/, label: t('password.requirements.includeNumber') },
    { re: /[a-z]/, label: t('password.requirements.includeLowercase') },
    { re: /[A-Z]/, label: t('password.requirements.includeUppercase') },
    {
      re: /[$&+,:;=?@#|'<>.^*()%!-_]/,
      label: t('password.requirements.includeSpecial'),
    },
  ];

  function getStrength(password: string) {
    let multiplier = password.length > 5 ? 0 : 1;

    requirements.forEach((requirement) => {
      if (!requirement.re.test(password)) {
        multiplier += 1;
      }
    });

    return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
  }

  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(value)}
    />
  ));

  const strength = getStrength(value);
  const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red';

  return (
    <Popover
      opened={popoverOpened}
      position="bottom"
      width="target"
      transitionProps={{ transition: 'pop' }}
    >
      <Popover.Target>
        <div
          onFocusCapture={() => setPopoverOpened(true)}
          onBlurCapture={() => setPopoverOpened(false)}
        >
          <PasswordInput
            {...props}
            onChange={(event) => {
              setValue(event.currentTarget.value);
              props.onChange?.(event);
            }}
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Progress color={color} value={strength} size={5} mb="xs" />
        <PasswordRequirement
          label={t('password.requirements.minLength')}
          meets={value.length > 5}
        />
        {checks}
      </Popover.Dropdown>
    </Popover>
  );
}
